/**
 * The engine's only transition (CONTEXT.md "Apply", SPEC.md §4.3).
 *
 * `apply` feeds one chosen option to the state, then runs every automatic step — turn
 * phases, challenge resolution, game state checks and the bag — until the next
 * `ChoiceRequest` surfaces. There is no phase API and no separate "advance" call: between
 * applies the state always sits on exactly one pending decision.
 *
 * The loop is driven by the explicit resolution stack in `GameState.stack`. `stepFrame`
 * takes the top frame and either performs one automatic step (returning `null`, so the
 * loop continues) or returns the request that frame is waiting on. No game logic lives in
 * suspended JS call frames, so a mid-turn state clones and serializes like any other.
 */
import {
  bagItemsFor,
  collectTriggers,
  expireTriggers,
  nextBagHolder,
  takeBagItem,
} from './bag.ts';
import { answerCustomChoice } from './abilities/custom.ts';
import {
  answerDiscard,
  answerMay,
  answerMode,
  chooseDistributeAmount,
  chooseDistributeTarget,
  choosePlayer,
  chooseTarget,
  effectRequest,
  expireModifiers,
  finishDistribute,
  finishTarget,
  pushAbility,
  pushEffectFrame,
  startActivation,
  stepEffectFrame,
} from './abilities/interpret.ts';
import type { Action, ChoiceOption, ChoiceRequest } from './choices.ts';
import { option } from './choices.ts';
import { INK_PAYMENT, expireCostModifiers } from './costs.ts';
import type { GameEvent } from './events.ts';
import { bagWindowOpen, settle } from './gsc.ts';
import type { InstanceId, PlayerIndex } from './ids.ts';
import type { Frame, FrameKind, GameResult, GameState } from './state.ts';
import { instanceAt, nextPlayer, playerAt } from './state.ts';
import { returnTemporaryShifts } from './stacks.ts';
import {
  declareChallenge,
  describeAction,
  drawStep,
  inkCard,
  inkCardFromDiscard,
  legalChallengeTargets,
  legalMulliganOptions,
  legalTurnActions,
  moveCharacter,
  playCard,
  playFromZonePermission,
  quest,
  readyStep,
  resolveChallengeDamage,
  runGameStateChecks,
  setStep,
} from './rules.ts';
import {
  drawCards,
  effectiveStrengthWhenBanished,
  emit,
  findInHandByDef,
  hadCardsUnderWhenBanished,
  maintainDeckTopReveals,
  moveInstance,
  shuffleDeck,
  stopLooking,
  underCardsWhenBanished,
} from './zones.ts';

/** Any engine-detected impossibility. */
export class EngineError extends Error {}

/** The chosen key is not one of the pending request's options. */
export class IllegalActionError extends EngineError {}

/**
 * Guard against a rules bug spinning forever. A single `apply` runs a handful of automatic
 * steps; hundreds means something never reached a decision.
 */
const MAX_STEPS_PER_APPLY = 1024;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** The options of the pending `ChoiceRequest` — the AI's moves and the UI's affordances. */
export function legalActions(state: GameState): readonly ChoiceOption[] {
  return state.pending.options;
}

/** Just the canonical keys, in the same order. */
export function legalActionKeys(state: GameState): readonly string[] {
  return state.pending.options.map((each) => each.key);
}

/** Whether the game has ended. A terminal state's pending request is `game-over`. */
export function isTerminal(state: GameState): boolean {
  return state.result !== null;
}

/** How the game ended, or `null` while it is still running. */
export function result(state: GameState): GameResult | null {
  return state.result;
}

/**
 * Apply one option **in place** and run on until the next decision. Returns the events
 * emitted by this transition (also left on `state.events`).
 *
 * Callers that need the old state clone first — the engine keeps no undo stack
 * (SPEC.md §4.2).
 */
export function apply(state: GameState, key: string): readonly GameEvent[] {
  if (state.result !== null) {
    throw new IllegalActionError(`The game is over; ${key} cannot be applied`);
  }
  const chosen = state.pending.options.find((each) => each.key === key);
  if (chosen === undefined) {
    throw new IllegalActionError(
      `${key} is not legal here; expected one of [${state.pending.options
        .map((each) => each.key)
        .join(', ')}]`,
    );
  }
  state.events = [];
  state.bag.eventCursor = 0;
  handleChoice(state, chosen.action);
  advance(state);
  return state.events;
}

/**
 * Run automatic steps until a decision surfaces. Exported for `createGame`, which uses it
 * to reach the game's first `ChoiceRequest`; nothing else should need it.
 */
export function advance(state: GameState): void {
  for (let step = 0; step < MAX_STEPS_PER_APPLY; step += 1) {
    // A face-up deck top follows the deck (`reveal-deck-top`), so the knowledge is topped up
    // between steps: whatever a draw, a shuffle or a tuck just put on top is face up before
    // the next decision surfaces, and before any ability reads the state.
    maintainDeckTopReveals(state);
    // Anything emitted since the last step may have met a trigger's condition (CR §7.7.2).
    collectTriggers(state);
    if (state.result !== null) {
      state.pending = { kind: 'game-over', player: -1, options: [] };
      return;
    }
    const frame = state.stack[state.stack.length - 1];
    if (frame === undefined) throw new EngineError('The resolution stack is empty');
    // A waiting trigger resolves before the game moves on — this is what makes a turn action
    // complete fully, bag emptied, before the next one is offered (CR §3.3, §7.7.4).
    if (state.bag.items.length > 0 && bagWindowOpen(frame)) {
      push(state, { kind: 'bag', holder: state.activePlayer });
      continue;
    }
    const request = stepFrame(state, frame);
    if (request !== null) {
      // A decision must never surface over an unsettled board (CR §1.8.2). Every turn action
      // settles at its own call site in `handleChoice`, but the ability interpreter has no
      // such site: `runNode` mutates the board and the frame then pauses for a target, so an
      // effect that dealt lethal damage would offer the dying character as the next target.
      // Settling only here, rather than each time round the loop, keeps the check where the
      // rules put it — after a resolution, not between an effect's internal steps.
      const emitted = state.events.length;
      settle(state);
      // If it banished something, the request was built against a board that no longer
      // exists — go round again and rebuild it. `state.result` is handled at the top.
      if (state.result !== null || state.events.length !== emitted) continue;
      state.pending = request;
      return;
    }
  }
  throw new EngineError(`No choice surfaced within ${MAX_STEPS_PER_APPLY} steps`);
}

// ---------------------------------------------------------------------------
// The resolution stack
// ---------------------------------------------------------------------------

function push(state: GameState, frame: Frame): void {
  state.stack.push(frame);
}

function pop(state: GameState, expected: FrameKind): void {
  const frame = state.stack.pop();
  if (frame === undefined || frame.kind !== expected) {
    throw new EngineError(`Expected a ${expected} frame on top of the resolution stack`);
  }
}

function topFrame<K extends FrameKind>(state: GameState, expected: K): Extract<Frame, { kind: K }> {
  const frame = state.stack[state.stack.length - 1];
  if (frame === undefined || frame.kind !== expected) {
    throw new EngineError(`Expected a ${expected} frame on top of the resolution stack`);
  }
  return frame as Extract<Frame, { kind: K }>;
}

/**
 * Perform one step of the top frame. Returns the `ChoiceRequest` that frame is waiting on,
 * or `null` if it made progress on its own and the loop should continue.
 */
function stepFrame(state: GameState, frame: Frame): ChoiceRequest | null {
  switch (frame.kind) {
    case 'game':
      return stepGame(state);
    case 'mulligan':
      return mulliganRequest(state, frame);
    case 'turn-start':
      return stepTurnStart(state, frame);
    case 'main':
      return mainRequest(state);
    case 'challenge':
      return stepChallenge(state, frame);
    case 'bag':
      return stepBag(state, frame);
    case 'resolve-ability':
      return stepResolveAbility(state, frame);
    case 'effect':
      return stepEffectFrame(state, frame);
    case 'enter-exerted':
      return enterExertedRequest(state, frame);
    case 'turn-end':
      return stepTurnEnd(state, frame);
  }
}

/**
 * "This character may enter play exerted" (Bodyguard, CR §8.2.1). A yes/no on the way into
 * play, before any "when you play" trigger resolves — which is why it is a frame of its own
 * rather than something the bag could interleave with.
 */
function enterExertedRequest(
  state: GameState,
  frame: Extract<Frame, { kind: 'enter-exerted' }>,
): ChoiceRequest {
  return {
    kind: 'may',
    player: instanceAt(state, frame.instance).controller,
    options: [option({ kind: 'may', accept: true }), option({ kind: 'may', accept: false })],
  };
}

function mulliganRequest(state: GameState, frame: Extract<Frame, { kind: 'mulligan' }>): ChoiceRequest {
  return {
    kind: 'mulligan',
    player: frame.player,
    options: legalMulliganOptions(state, frame.player, frame.selected),
  };
}

function mainRequest(state: GameState): ChoiceRequest {
  return { kind: 'turn-action', player: state.activePlayer, options: legalTurnActions(state) };
}

function challengeTargetRequest(
  state: GameState,
  frame: Extract<Frame, { kind: 'challenge' }>,
): ChoiceRequest | null {
  const targets = legalChallengeTargets(state, frame.challenger);
  if (targets.length === 0) return null;
  return {
    kind: 'challenge-target',
    player: state.activePlayer,
    options: targets.map((target) => option({ kind: 'challenge-target', instance: target.id })),
  };
}

/**
 * The bag resolving (CR §7.7.4).
 *
 * Every visit starts by settling the game state check cascade — a completed check immediately
 * runs again, and only when it finds nothing do players resolve bag abilities (CR §1.8.3). So a
 * banish-on-damage that fires new triggers re-runs the GSC to quiet before the bag continues,
 * and a win or loss found mid-cascade ends the game with the bag still full.
 *
 * Then the bag passes: the active player has the first say, the holder keeps choosing while
 * they still control a waiting trigger — including one they just added — and only then does it
 * move to the next player in turn order. The holder orders **their own** triggers only, which
 * is a choice only when they have two or more waiting; a lone trigger resolves without asking.
 */
function stepBag(state: GameState, frame: Extract<Frame, { kind: 'bag' }>): ChoiceRequest | null {
  settle(state);
  if (state.result !== null) return null;
  const holder = nextBagHolder(state, frame.holder);
  if (holder === null) {
    pop(state, 'bag');
    return null;
  }
  frame.holder = holder;
  const waiting = bagItemsFor(state, holder);
  const only = waiting[0];
  if (waiting.length === 1 && only !== undefined) {
    startResolving(state, only.id);
    return null;
  }
  return bagOrderRequest(state, holder);
}

function bagOrderRequest(state: GameState, holder: PlayerIndex): ChoiceRequest {
  return {
    kind: 'bag-order',
    player: holder,
    options: bagItemsFor(state, holder).map((item) =>
      option({ kind: 'bag-order', item: item.id }),
    ),
  };
}

/** Take a bag item out of the bag and put its resolution on the resolution stack. */
function startResolving(state: GameState, item: number): void {
  pushAbility(state, takeBagItem(state, item));
}

/**
 * Resolve one ability in full (CR §6.7.2), then run the game state check that follows all of
 * an ability's effects (CR §6.7.5). Triggers it raised are already in the bag but wait their
 * turn — the `bag` frame underneath decides who resolves next.
 *
 * Two visits: the first opens the `effect` frame that walks the ability's IR (which may block
 * on player choices, and may push frames of its own); the second, once that frame has popped,
 * finishes the ability.
 */
function stepResolveAbility(
  state: GameState,
  frame: Extract<Frame, { kind: 'resolve-ability' }>,
): null {
  if (!frame.started) {
    frame.started = true;
    pushEffectFrame(state, frame.item);
    return null;
  }
  pop(state, 'resolve-ability');
  // Nobody is left looking at a deck once the ability that opened it is done (CR §7.9), so a
  // view can never name a deck card outside the moment the effect was reading it.
  stopLooking(state);
  emit(state, {
    type: 'ability-resolved',
    player: frame.item.controller,
    source: frame.item.source,
    defId: instanceAt(state, frame.item.source).defId,
    item: frame.item.id,
    ability: frame.item.label,
  });
  // An action card sits in the Play zone only while its effect resolves (CR §5.4.1.2) — to the
  // discard ordinarily, or to the bottom of the deck when `finishTo` redirects it (Ursula -
  // Deceiver of All's WHAT A DEAL, ticket 127: "put that card on the bottom of your deck
  // instead of into your discard"). Its own clarification places the card there only once this
  // finishing step runs, never by way of the discard.
  if (frame.item.origin === 'action' && instanceAt(state, frame.item.source).zone === 'play') {
    if (frame.item.finishTo === 'bottom-of-deck') {
      moveInstance(state, frame.item.source, 'deck', 'bottom');
    } else {
      moveInstance(state, frame.item.source, 'discard');
    }
  }
  settle(state);
  return null;
}

/**
 * Rebuild the pending request from the top frame.
 *
 * The state *carries* its `ChoiceRequest` as data, so options are computed when the
 * request surfaces — not on every read. Code that edits the board behind the engine's back
 * (only the test fixtures do) must call this afterwards. Deliberately not part of the
 * package's public API.
 */
export function recomputePending(state: GameState): void {
  // Arranging a board is a move like any other: a deck a `reveal-deck-top` static now covers
  // has its top card face up before the first request is offered.
  maintainDeckTopReveals(state);
  if (state.result !== null) return;
  const frame = state.stack[state.stack.length - 1];
  if (frame === undefined) throw new EngineError('The resolution stack is empty');
  const request =
    frame.kind === 'mulligan'
      ? mulliganRequest(state, frame)
      : frame.kind === 'main'
        ? mainRequest(state)
        : frame.kind === 'challenge' && frame.step === 'choose-target'
          ? challengeTargetRequest(state, frame)
          : frame.kind === 'bag' && bagItemsFor(state, frame.holder).length > 1
            ? bagOrderRequest(state, frame.holder)
            : frame.kind === 'effect'
              ? effectRequest(state, frame)
              : frame.kind === 'enter-exerted'
                ? enterExertedRequest(state, frame)
                : frame.kind === 'turn-start' && frame.step === 'ready-choice'
                  ? readyChoiceRequest(state, frame)
                  : null;
  if (request === null) {
    throw new EngineError(`A ${frame.kind} frame is not waiting on a choice`);
  }
  state.pending = request;
}

/** The bottom frame: it is never popped, and stepping it starts the next turn. */
function stepGame(state: GameState): null {
  if (state.phase === 'setup') finishSetup(state);
  beginTurn(state);
  return null;
}

/**
 * The third pass of altering hands (CR §2.2.2): every player who put cards under their
 * deck shuffles it. Done after all players have altered, not per player.
 */
function finishSetup(state: GameState): void {
  for (const player of state.players) {
    if (player.alteredHand) shuffleDeck(state, player.index);
  }
}

function beginTurn(state: GameState): void {
  state.phase = 'start-of-turn';
  emit(state, { type: 'turn-began', player: state.activePlayer, turn: state.turn });
  emit(state, { type: 'phase-began', player: state.activePlayer, phase: 'start-of-turn' });
  push(state, { kind: 'turn-start', step: 'ready', readyChoice: null });
}

/** Start-of-Turn phase (CR §3.2): a game state check ends every step. */
function stepTurnStart(
  state: GameState,
  frame: Extract<Frame, { kind: 'turn-start' }>,
): ChoiceRequest | null {
  switch (frame.step) {
    case 'ready': {
      // "Until the start of your next turn" ends here, before anything else this turn
      // happens (CR §3.2, §6.1.13).
      expireModifiers(state, 'start-of-turn');
      expireTriggers(state, 'start-of-turn');
      // Payment modifiers cross the same boundary: "opponents can't play actions until the
      // start of your next turn" ends exactly here (ticket 09, `costs.ts`).
      expireCostModifiers(state, 'start-of-turn');
      const pending = readyStep(state);
      if (pending !== null) {
        // A live ready-cap left more characters eligible than it allows — the affected player
        // chooses which of them ready (Marshmallow - Cranky Climber). Everything else this
        // step touches has already readied; the game state check that follows the Ready step
        // waits for that choice to finish, alongside the rest of the step (see `ready-choice`).
        frame.readyChoice = { eligible: [...pending.eligible], cap: pending.cap, chosen: [] as InstanceId[] };
        frame.step = 'ready-choice';
        return readyChoiceRequest(state, frame);
      }
      runGameStateChecks(state);
      frame.step = 'set';
      return null;
    }
    case 'ready-choice': {
      const rc = frame.readyChoice;
      if (rc === null) throw new EngineError('turn-start frame is missing its ready choice');
      const remaining = rc.eligible.filter((id) => !rc.chosen.includes(id));
      if (rc.chosen.length >= rc.cap || remaining.length === 0) {
        frame.readyChoice = null;
        runGameStateChecks(state);
        frame.step = 'set';
        return null;
      }
      return readyChoiceRequest(state, frame);
    }
    case 'set':
      setStep(state);
      runGameStateChecks(state);
      frame.step = 'draw';
      return null;
    case 'draw':
      drawStep(state);
      runGameStateChecks(state);
      pop(state, 'turn-start');
      // The turn's remaining frames, bottom first: the end phase waits under the main phase.
      push(state, { kind: 'turn-end', step: 'triggers' });
      push(state, { kind: 'main' });
      state.phase = 'main';
      emit(state, { type: 'phase-began', player: state.activePlayer, phase: 'main' });
      return null;
  }
}

/**
 * The pending ready-cap decision (CR §6.6.5): pick one of the still-exerted eligible
 * characters to ready. Only offered while `readyChoice` is live and short of its cap —
 * `stepTurnStart`'s `ready-choice` case retires the frame the moment it is not.
 */
function readyChoiceRequest(
  state: GameState,
  frame: Extract<Frame, { kind: 'turn-start' }>,
): ChoiceRequest {
  const rc = frame.readyChoice;
  if (rc === null) throw new EngineError('turn-start frame is missing its ready choice');
  const remaining = rc.eligible.filter((id) => !rc.chosen.includes(id));
  return {
    kind: 'ready-choice',
    player: state.activePlayer,
    options: remaining.map((id) => option({ kind: 'ready-choose', instance: id })),
  };
}

/** Challenge (CR §4.6): declaration, then — with the bag window between — damage. */
function stepChallenge(state: GameState, frame: Extract<Frame, { kind: 'challenge' }>): ChoiceRequest | null {
  if (frame.step === 'choose-target') {
    const request = challengeTargetRequest(state, frame);
    if (request === null) {
      // Unreachable: `legalTurnActions` only offers a challenger that has a target.
      pop(state, 'challenge');
      return null;
    }
    return request;
  }
  const defender = frame.target;
  if (defender === null) throw new EngineError('Challenge frame is resolving with no target');
  // A card removed from the challenge during the declaration's bag window — banished by a
  // trigger, say — ends the challenge early, with no damage step (CR §4.6.9).
  const bothPresent =
    instanceAt(state, frame.challenger).zone === 'play' && instanceAt(state, defender).zone === 'play';
  if (bothPresent) resolveChallengeDamage(state, frame.challenger, defender);
  // The check runs with the challenge frame **still on the stack**, so a card banished by it
  // knows it was banished in a challenge and by whom (CR §4.6.6, `zones.ts`). Nothing else
  // reads the frame during a check — it compares damage against {W} — and the frame is gone
  // before the next step, so no bag window ever sees it.
  runGameStateChecks(state);
  pop(state, 'challenge');
  if (bothPresent) announceChallengeBanishes(state, frame.challenger, defender);
  return null;
}

/**
 * "Banished in a challenge" (CR §4.6.6), as an event.
 *
 * Whether a card was banished *by a challenge* is not a fact the game state check can see — it
 * banishes anything with lethal damage, from wherever the damage came. It is a fact this frame
 * knows: the two participants were in play when the damage step began, and whichever of them is
 * in a discard once the check has quieted was banished by this challenge. Emitted for each,
 * with the other card as `by`, which is what tells "when this character **is** banished in a
 * challenge" apart from "whenever this character **banishes** another character in a challenge".
 */
function announceChallengeBanishes(
  state: GameState,
  challenger: InstanceId,
  defender: InstanceId,
): void {
  const sides = [
    { instance: challenger, other: defender, role: 'challenger' as const },
    { instance: defender, other: challenger, role: 'defender' as const },
  ];
  for (const side of sides) {
    const card = instanceAt(state, side.instance);
    if (card.zone !== 'discard') continue;
    // The per-turn fact "was banished in a challenge this turn" is written exactly where the
    // event is announced, so the two can never disagree; the Ready step clears it.
    state.banishedInChallengeThisTurn.push(side.instance);
    emit(state, {
      type: 'banished-in-challenge',
      player: card.controller,
      instance: side.instance,
      defId: card.defId,
      by: side.other,
      role: side.role,
      hadCardsUnder: hadCardsUnderWhenBanished(state, side.instance),
      underCards: underCardsWhenBanished(state, side.instance),
      effectiveStrength: effectiveStrengthWhenBanished(state, side.instance),
    });
  }
}

/**
 * End-of-Turn phase (CR §3.4), in two steps so the bag empties before the turn passes:
 * "at the end of the turn" triggers are added and resolved, and only **when the bag is empty**
 * does the turn end with a final game state check.
 */
function stepTurnEnd(state: GameState, frame: Extract<Frame, { kind: 'turn-end' }>): null {
  if (frame.step === 'triggers') {
    state.phase = 'end-of-turn';
    emit(state, { type: 'phase-began', player: state.activePlayer, phase: 'end-of-turn' });
    // "At the end of your turn, remove all damage from this character and return this card to
    // your hand" — Temporary Shift (CR 2.2 §8.10.8). It happens in this phase, before the
    // phase's own triggers are collected, so whatever it sets off is ordered with them.
    returnTemporaryShifts(state, state.activePlayer);
    runGameStateChecks(state);
    // The bag window opens on the way back round: end-of-turn triggers resolve while this
    // player is still the active player, which is what makes them theirs to order.
    frame.step = 'finish';
    return null;
  }
  // "This turn" effects end in the End-of-Turn phase, after its triggers (CR §3.4). The other
  // durations expire at their own boundaries — see `modifiers.ts`.
  expireModifiers(state, 'end-of-turn');
  // …and for a delayed triggered ability that only lasted the turn ("whenever one of your
  // characters quests this turn, …").
  expireTriggers(state, 'end-of-turn');
  // The same moment for payment modifiers — a "this turn" cost reduction stops applying here
  // whether or not it was ever spent (ticket 09, `costs.ts`).
  expireCostModifiers(state);
  // The one check that can lose you the game for an empty deck (CR §1.8.1.2).
  runGameStateChecks(state, { turnEnding: true });
  if (state.result !== null) return null;
  emit(state, { type: 'turn-ended', player: state.activePlayer, turn: state.turn });
  pop(state, 'turn-end');
  state.activePlayer = nextPlayer(state, state.activePlayer);
  state.turn += 1;
  return null;
}

// ---------------------------------------------------------------------------
// Answering the pending choice
// ---------------------------------------------------------------------------

function handleChoice(state: GameState, action: Action): void {
  switch (action.kind) {
    case 'mulligan-bottom': {
      const frame = topFrame(state, 'mulligan');
      const card = findInHandByDef(state, frame.player, action.defId, frame.selected);
      if (card === undefined) {
        throw new IllegalActionError(`No card left to bottom for ${describeAction(action)}`);
      }
      frame.selected.push(card.id);
      return;
    }
    case 'mulligan-done': {
      const frame = topFrame(state, 'mulligan');
      finishMulligan(state, frame);
      pop(state, 'mulligan');
      return;
    }
    case 'ink':
      inkCard(state, state.activePlayer, action.defId);
      runGameStateChecks(state);
      return;
    case 'ink-from-discard':
      inkCardFromDiscard(state, state.activePlayer, action.instance);
      runGameStateChecks(state);
      return;
    case 'play':
      // The option carries the announced payment method (CR §4.3.1.2); absent means ink.
      playCard(state, state.activePlayer, action.defId, action.payment ?? INK_PAYMENT);
      runGameStateChecks(state);
      return;
    case 'play-from-zone':
      playFromZonePermission(state, state.activePlayer, action.instance, action.free);
      runGameStateChecks(state);
      return;
    case 'quest':
      quest(state, action.instance);
      runGameStateChecks(state);
      return;
    case 'move':
      moveCharacter(state, state.activePlayer, action.instance, action.location);
      runGameStateChecks(state);
      return;
    case 'challenge':
      push(state, {
        kind: 'challenge',
        challenger: action.instance,
        step: 'choose-target',
        target: null,
      });
      return;
    case 'challenge-target': {
      const frame = topFrame(state, 'challenge');
      declareChallenge(state, frame.challenger, action.instance);
      frame.target = action.instance;
      frame.step = 'resolve';
      // CR §4.6.4: a game state check follows the Challenge Declaration step.
      runGameStateChecks(state);
      return;
    }
    case 'bag-order': {
      // Only the holder's own triggers are ever offered, so this needs no further checking.
      topFrame(state, 'bag');
      startResolving(state, action.item);
      return;
    }
    case 'ready-choose': {
      // The option list already named only eligible, unchosen characters, so there is nothing
      // further to check here — same discipline as `bag-order`. Whether this finishes the
      // decision (cap reached, or nobody eligible left) is `stepTurnStart`'s `ready-choice`
      // case's call, made the next time that frame is stepped.
      const frame = topFrame(state, 'turn-start');
      const rc = frame.readyChoice;
      if (rc === null) {
        throw new IllegalActionError(`No ready-step choice pending for ${describeAction(action)}`);
      }
      rc.chosen.push(action.instance);
      const card = instanceAt(state, action.instance);
      card.exerted = false;
      emit(state, {
        type: 'card-readied',
        player: state.activePlayer,
        instance: action.instance,
        defId: card.defId,
      });
      return;
    }
    case 'activate':
      // CR §7.7: an activated ability never enters the bag — it goes straight on the stack.
      startActivation(state, {
        instance: action.instance,
        ability: action.ability,
        ...(action.banish === undefined ? {} : { banish: action.banish }),
        ...(action.exert === undefined ? {} : { exert: action.exert }),
        ...(action.discard === undefined ? {} : { discard: action.discard }),
      });
      runGameStateChecks(state);
      return;
    case 'ability-target': {
      // Two decisions raise this same action shape: an ordinary `target` node's pick, and a
      // `distribute-remove-damage` decision's "which character next" round (ticket 135) — told
      // apart by which the frame is actually waiting on, the same discipline `may`'s two
      // sources already use just below.
      const frame = topFrame(state, 'effect');
      if (frame.awaiting?.kind === 'distribute') chooseDistributeTarget(frame, action.instance);
      else chooseTarget(state, frame, action.instance);
      return;
    }
    case 'ability-target-done': {
      const frame = topFrame(state, 'effect');
      if (frame.awaiting?.kind === 'distribute') finishDistribute(frame);
      else finishTarget(state, frame);
      return;
    }
    case 'ability-amount':
      chooseDistributeAmount(state, topFrame(state, 'effect'), action.value);
      return;
    case 'choose-player':
      choosePlayer(state, topFrame(state, 'effect'), action.player);
      return;
    case 'may': {
      // Two frames raise a yes/no: an effect's `may` node, and Bodyguard's entry choice.
      const frame = state.stack[state.stack.length - 1];
      if (frame !== undefined && frame.kind === 'enter-exerted') {
        instanceAt(state, frame.instance).exerted = action.accept;
        pop(state, 'enter-exerted');
        return;
      }
      answerMay(state, topFrame(state, 'effect'), action.accept);
      return;
    }
    case 'ability-mode':
      answerMode(topFrame(state, 'effect'), action.mode);
      return;
    case 'custom-choice':
      // Only the transcript is written here; the generator is re-driven from it on the next
      // step of the effect frame (SPEC.md §4.3's replay-to-resume).
      answerCustomChoice(state, topFrame(state, 'effect'), action.token);
      return;
    case 'discard':
      answerDiscard(state, topFrame(state, 'effect'), action.defId);
      return;
    case 'end-turn':
      pop(state, 'main');
      return;
  }
}

/**
 * Finish one player's hand alteration (CR §2.2.2): the chosen cards go under the deck
 * unrevealed, then that player draws back up to their opening hand size. The shuffle
 * happens later, once every player has altered.
 */
function finishMulligan(state: GameState, frame: Extract<Frame, { kind: 'mulligan' }>): void {
  for (const id of frame.selected) moveInstance(state, id, 'deck', 'bottom');
  drawCards(state, frame.player, frame.selected.length);
  if (frame.selected.length > 0) playerAt(state, frame.player).alteredHand = true;
  emit(state, { type: 'hand-altered', player: frame.player, count: frame.selected.length });
}
